import React, { useCallback, useEffect, useState, useRef } from "react";
import { GetMyLeads } from "../../Leads/Services/LeadService";
import PhoneVerificationDrawer from "../../Leads/Modals/PhoneVerificationDrawer";
import {
  User,
  Search,
  RefreshCcw,
  Phone,
  Globe,
  Link,
  Copy,
  Check,
  Pencil,
  ChevronDown,
  MoreVertical,
  Eye,
  History,
} from "lucide-react";
import { formatCTC } from "../../../../Utils/FormUtils";
import ViewResumeDrawer from "../../../../Common/Recruitment/Modals/ViewResumeDrawer";
import InitiateCallDrawer from "../Modals/InitiateCallDrawer";
import { useNavigate } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import RecruitmentHistoryDrawer from "../Modals/RecruitmentHistoryDrawer";

const MyLeads = () => {
  const [chunkNumber, setChunkNumber] = useState(1);
  const [totalChunks, setTotalChunks] = useState(1);
  const [allLeads, setAllLeads] = useState([]);
  
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [chunkLoading, setChunkLoading] = useState(false);
  const showBodyLoader = initialLoading;

  const [expandedLead, setExpandedLead] = useState(null);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [copiedLeadId, setCopiedLeadId] = useState(null);
  const [selectedResume, setSelectedResume] = useState(null);
  const [openResumeDrawer, setOpenResumeDrawer] = useState(false);
  const [openPhoneDrawer, setOpenPhoneDrawer] = useState(false);
  const [openCallDrawer, setOpenCallDrawer] = useState(false);
  const [callLead, setCallLead] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [pageSize, setPageSize] = useState(50);
  const [localResults, setLocalResults] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [dbSearching, setDbSearching] = useState(false);
  const displayedTotalRecords = searchPerformed
    ? localResults.length
    : totalRecords;
  const totalPages = Math.ceil(displayedTotalRecords / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const showChunkLoader = chunkLoading;
  const [openHistoryDrawer, setOpenHistoryDrawer] = useState(false);
  const [selectedHistoryLead, setSelectedHistoryLead] = useState(null);
  const chunkOffset =
  ((page - 1) * pageSize) % 500;

const visibleLeads =
  searchPerformed
    ? localResults.slice(
        startIndex,
        endIndex,
      )
    : allLeads.slice(
        chunkOffset,
        chunkOffset + pageSize,
      );

  const fetchLeads = useCallback(
  async (chunk = 1, reset = false, searchParam = null) => {
    if (reset) {
      setInitialLoading(true);
    } else {
      setChunkLoading(true);
    }

    try {
      const query =
        (searchParam !== null
          ? searchParam
          : activeSearch) || "";

      const res = await GetMyLeads(
        chunk,
        query,
      );

      if (res?.status === "success") {
        setAllLeads(res.leads || []);

        setChunkNumber(chunk);

        setTotalChunks(
          res.totalChunks || 1,
        );

        setTotalRecords(
          res.totalRecords || 0,
        );
      }
    } finally {
      if (reset) {
        setInitialLoading(false);
      } else {
        setChunkLoading(false);
      }
    }
  },
  [activeSearch],
);

  useEffect(() => {
    fetchLeads(1, true);
  }, [fetchLeads]);

  useEffect(() => {
  const handleOutside = () => {
    setOpenActionMenu(null);
  };

  document.addEventListener(
    "click",
    handleOutside
  );

  return () => {
    document.removeEventListener(
      "click",
      handleOutside
    );
  };
}, []);

  const handleReset = () => {
    setSearch("");

    setPage(1);

    setLocalResults([]);

    setSearchPerformed(false);

    // ONLY do this
    setActiveSearch("");
  };

  const handleCopyPhone = async (leadId, phoneNo) => {
    if (!phoneNo) return;

    try {
      await navigator.clipboard.writeText(phoneNo);
      setCopiedLeadId(leadId);

      window.setTimeout(() => {
        setCopiedLeadId((current) => (current === leadId ? null : current));
      }, 1800);
    } catch (err) {
      console.error("Failed to copy phone number:", err);
    }
  };

  const handlePageChange = async (
  newPage,
) => {
  if (chunkLoading) return;

  setPage(newPage);

  const recordPosition =
    newPage * pageSize;

  const requiredChunk =
    Math.ceil(
      recordPosition / 500,
    );

  if (
    requiredChunk !== chunkNumber
  ) {
    await fetchLeads(
      requiredChunk,
      false,
    );
  }
};

  const LeadsSkeleton = ({ rows = 8 }) => {
    return (
      <SkeletonTheme baseColor="#f1f5f9" highlightColor="#ffffff">
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className="
            grid grid-cols-12
            gap-4
            px-6 py-4
            border-b border-slate-100
          "
          >
            <div className="col-span-3 flex gap-3">
              <Skeleton circle width={40} height={40} />

              <div className="flex-1">
                <Skeleton height={16} width="70%" />
                <Skeleton height={12} width="90%" className="mt-2" />
              </div>
            </div>

            <div className="col-span-2">
              <Skeleton height={16} />
            </div>

            <div className="col-span-2">
              <Skeleton height={16} />
              <Skeleton height={12} className="mt-2" />
            </div>

            <div className="col-span-1">
              <Skeleton height={16} />
            </div>

            <div className="col-span-1">
              <Skeleton height={24} />
            </div>

            <div className="col-span-1">
              <Skeleton height={16} />
            </div>

            <div className="col-span-1">
              <Skeleton height={16} />
            </div>

            <div className="col-span-1 flex justify-center">
              <Skeleton width={32} height={32} />
            </div>
          </div>
        ))}
      </SkeletonTheme>
    );
  };

  const handleLocalSearch = async () => {
    const value = search.trim().toLowerCase();

    if (!value) {
      setLocalResults([]);
      setSearchPerformed(false);
      return;
    }

    const results = allLeads.filter((lead) => {
      return (
        lead.name?.toLowerCase().includes(value) ||
        lead.phoneNo?.toLowerCase().includes(value) ||
        lead.email?.toLowerCase().includes(value) ||
        lead.city?.toLowerCase().includes(value) ||
        lead.state?.toLowerCase().includes(value)
      );
    });

    if(results.length > 0) {
      setPage(1);
      setSearchPerformed(true);
      setLocalResults(results);
      return;
    }

    try
    {
      setDbSearching(true);
      setPage(1);
      setLocalResults([]);
      setSearchPerformed(false);
      setActiveSearch(value);
      await fetchLeads(1, true, value);
    }
    catch(error){
      setLocalResults([]);
      setSearchPerformed(true);
      setPage(1);
    }
    finally{
      setDbSearching(false);
    }

  };

  const searchDatabase = () => {
    const query = search.trim();

    if (!query) return;

    setPage(1);
    setLocalResults([]);
    setSearchPerformed(false);
    setActiveSearch(query);

    (async () => {
      try {
        setDbSearching(true);
        await fetchLeads(1, true, query);
      } catch (err) {
        console.error("DB search failed", err);
      } finally {
        setDbSearching(false);
      }
    })();
  };

  return (
    <>
      <div className="p-2 space-y-3">
        {/* ================= TABLE TOOLBAR ================= */}
        <div
          className="max-w-8xl mx-auto bg-gradient-to-b from-white to-slate-50
                    border border-slate-200/70
                    shadow-sm
                    hover:shadow-md
                    hover:border-blue-200 rounded-xl px-5 py-3"
        >
          <div className="flex items-center justify-between gap-4">
            {/* LEFT */}
            <div className="flex items-center gap-4 flex-1">
              {/* SEARCH */}
              <div className="flex items-center w-full max-w-md bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search leads..."
                  className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleLocalSearch();
                    }
                  }}
                />

                <button
                  title="Search"
                  onClick={() => {
                    handleLocalSearch();
                  }}
                  className="h-10 w-10 border-l border-slate-200 flex items-center justify-center hover:bg-slate-100 transition"
                >
                  <Search size={17} className="text-slate-500" />
                </button>

                {(search || activeSearch || searchPerformed) && (
                  <button
                    title="Reset"
                    onClick={handleReset}
                    className="h-10 w-10 border-l border-slate-200 flex items-center justify-center hover:bg-slate-100 transition"
                  >
                    <RefreshCcw size={16} className="text-red-500" />
                  </button>
                )}
              </div>

              {/* TOTAL */}
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-sm text-slate-500">Total Leads</span>

                <span className="h-7 px-2.5 inline-flex items-center justify-center rounded-lg bg-slate-100 border border-slate-200 text-sm font-semibold text-slate-700">
                  {displayedTotalRecords}
                </span>
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-5">
              {/* SHOWING */}
              <div className="text-sm text-slate-600 whitespace-nowrap">
                Showing{" "}
                <span className="font-semibold text-slate-800">
                  {displayedTotalRecords === 0
                    ? "0"
                    : `${startIndex + 1}-${Math.min(endIndex, displayedTotalRecords)}`}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-800">
                  {displayedTotalRecords}
                </span>
              </div>

              {/* ROWS */}
              <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
                <span className="text-sm text-slate-500 whitespace-nowrap">
                  Rows
                </span>

                <select
                  value={pageSize}
                  onChange={(e) => {
                    const rows = Number(e.target.value);
                    setPageSize(rows);
                    setPage(1);
                    const required = rows;
                    if (
                      required > allLeads.length &&
                      chunkNumber < totalChunks
                    ) {
                      fetchLeads(chunkNumber + 1);
                    }
                  }}
                  className="h-8 px-2 pr-7 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 focus:outline-none"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={125}>125</option>
                  <option value={250}>250</option>
                </select>
              </div>

              {/* ADD BUTTON */}
              <button
                onClick={() => setOpenPhoneDrawer(true)}
                className="h-9 px-4 rounded-xl bg-primary text-white text-sm font-medium transition whitespace-nowrap"
              >
                + Add Lead
              </button>
            </div>
          </div>
        </div>

        {/* ================= LEADS TABLE ================= */}
        <div className="max-w-8xl mx-auto bg-white border border-border rounded-xl shadow-sm flex flex-col h-[calc(100vh-220px)] overflow-visible">
          {/* HEADER */}
          <div className="grid grid-cols-12  gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sticky top-0 z-20 flex-shrink-0">
            <div className="col-span-3">Lead</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Location</div>
            <div className="col-span-1">Gender</div>
            <div className="col-span-1">Source</div>
            <div className="col-span-1 text-right">Created Date</div>
            <div className="col-span-1 text-right">Last Updated</div>
            <div className="col-span-1 text-center">Actions</div>
          </div>

          {/* BODY */}
          <div className="relative flex-1 overflow-y-auto divide-y-2 divide-slate-100">
            {showChunkLoader && (
              <div
                className="absolute inset-0 z-40 bg-white/80 backdrop-blur-sm overflow-hidden"
              >
                <LeadsSkeleton rows={pageSize} />
              </div>
            )}
            {/* CONTENT */}
            <div className="relative h-full">
              {showBodyLoader ? (
                <LeadsSkeleton rows={pageSize} />
              ) : visibleLeads.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center text-center">
                    <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                      <User size={24} className="text-slate-400" />
                    </div>

                    <h3 className="text-sm font-semibold text-slate-700">
                      No leads found
                    </h3>
                  </div>
                </div>
              ) : (
                <>
                  {visibleLeads.map((lead, index) => {
                    const hasResume = !!lead.resumePath;
                    const formatDate = (dateString) => {
                      if (!dateString) {
                        return { date: "-", time: "-" };
                      }
                      const dateObj = new Date(dateString);
                      if (isNaN(dateObj.getTime())) {
                        return { date: "-", time: "-" };
                      }

                      return {
                        date: dateObj.toLocaleDateString("en-In", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }),
                        time: dateObj.toLocaleTimeString("en-IN", {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        }),
                      };
                    };

                    const formatted = formatDate(lead.createdAT);
                    const updatedFormatted = formatDate(lead.lastUpdatedDate);
                    return (
                      <div key={lead.leadUID}>
                        {/* MAIN ROW */}
                        <div className="grid grid-cols-12 gap-4 px-6 py-4 items-start hover:bg-blue-50/30 transition-all duration-200 group">
                          {/* LEAD */}
                          <div className="col-span-3 flex items-start gap-3 min-w-0">
                            <div className=" h-10 w-10 flex-shrink-0 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center font-semibold  text-sm text-blue-700">
                              {lead.name?.charAt(0)}
                            </div>

                            <div
                              onClick={() =>
                                setExpandedLead(
                                  expandedLead === lead.leadUID
                                    ? null
                                    : lead.leadUID,
                                )
                              }
                              className="min-w-0 pt-0.5 cursor-pointer"
                            >
                              <div className="flex items-center gap-1.5">
                                <p className="font-semibold text-[15px] truncate text-blue-600 transition">
                                  {lead.name}
                                </p>

                                <ChevronDown
                                  size={14}
                                  className={`text-slate-400 transition-transform duration-300 ${
                                    expandedLead === lead.leadUID
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                />
                              </div>

                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {lead.email}
                              </p>
                            </div>
                          </div>

                          {/* PHONE */}
                          <div className="col-span-2 pt-1">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                              <Phone size={13} className="text-slate-400" />

                              <span
                                className="transition-colors font-medium tracking-wide"
                                title={lead.phoneNo}
                              >
                                {lead.phoneNo}
                              </span>

                              <button
                                type="button"
                                onClick={() =>
                                  handleCopyPhone(
                                    lead.leadUID || index,
                                    lead.phoneNo,
                                  )
                                }
                                className={`inline-flex h-6 w-6 items-center justify-center rounded-lg border transition ${
                                  copiedLeadId === (lead.leadUID || index)
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                                    : "border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                }`}
                                title={
                                  copiedLeadId === (lead.leadUID || index)
                                    ? "Copied"
                                    : "Copy phone number"
                                }
                                aria-label={`Copy phone number for ${lead.name}`}
                              >
                                {copiedLeadId === (lead.leadUID || index) ? (
                                  <Check size={12} />
                                ) : (
                                  <Copy size={12} />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* LOCATION */}
                          <div className="col-span-2 min-w-0 pt-0.5">
                            <p className="text-sm text-slate-700 truncate">
                              {lead.city}, {lead.state}
                            </p>

                            <p className="text-xs text-slate-400 truncate mt-0.5">
                              {lead.country} • {lead.pinCode}
                            </p>
                          </div>

                          {/* GENDER */}
                          <div className="col-span-1 pt-1">
                            <span className="text-sm text-slate-600">
                              {lead.gender}
                            </span>
                          </div>

                          {/* SOURCE */}
                          <div className="col-span-1 pt-0.5">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-[11px] font-medium text-blue-700 border border-blue-100">
                              <Link size={10} />
                              {lead.source}
                            </span>
                          </div>

                          {/* CREATED */}
                          <div className="col-span-1 flex items-start justify-end">
                            <div className="flex items-start gap-2">
                              {/* DATE */}
                              <div className="text-right pt-0.5">
                                <p className="text-sm text-slate-700 leading-none whitespace-nowrap">
                                  {formatted.date}
                                </p>

                                <p className="text-xs text-slate-400 mt-1 whitespace-nowrap">
                                  {formatted.time}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* UPDATED */}
                          <div className="col-span-1 flex items-start justify-end">
                            <div className="text-right pt-0.5">
                              <p className="text-sm text-slate-700 leading-none whitespace-nowrap">
                                {updatedFormatted.date}
                              </p>

                              <p className="text-xs text-slate-400 mt-1 whitespace-nowrap">
                                {updatedFormatted.time}
                              </p>
                            </div>
                          </div>

                          {/* ACTION MENU */}
                          <div className="col-span-1 flex justify-center relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenActionMenu(
                                  openActionMenu === lead.leadUID ? null : lead.leadUID
                                );
                              }}
                              className="h-8 w-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-500 
                                       hover:bg-slate-50 hover:border-blue-200 hover:text-slate-700 transition-all"
                            >
                              <MoreVertical size={15} />
                            </button>

                            {openActionMenu === lead.leadUID && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-10 right-0 z-50 w-44 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden py-1"
                              >
                                {/* VIEW */}
                                <button
                                  onClick={() => {
                                    setExpandedLead(
                                      expandedLead === lead.leadUID ? null : lead.leadUID,
                                    );
                                    setOpenActionMenu(null);
                                  }}
                                  className="w-full h-9 px-3 flex items-center gap-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition"
                                >
                                  <Eye size={14} />
                                  <span>View</span>
                                </button>

                                {/* RESUME */}
                                <button
                                  disabled={!hasResume}
                                  onClick={() => {
                                    if (!hasResume) return;
                                    setSelectedResume(lead);
                                    setOpenResumeDrawer(true);
                                    setOpenActionMenu(null);
                                  }}
                                  className={`w-full h-9 px-3 flex items-center gap-2 text-sm transition
                                              ${
                                                hasResume
                                                  ? `text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition`
                                                  : `text-slate-400 cursor-not-allowed`
                                              }
                                            `}
                                >
                                  <Globe size={14} />
                                  <span>
                                    {hasResume ? "Resume" : "No Resume"}
                                  </span>
                                </button>

                                {/* CALL */}
                                <button
                                  onClick={() => {
                                    setCallLead(lead);
                                    setOpenCallDrawer(true);
                                    setOpenActionMenu(null);
                                  }}
                                  className="w-full h-9 px-3 flex items-center gap-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition"
                                >
                                  <Phone size={14} />
                                  <span>Connect</span>
                                </button>

                                {/* EDIT */}
                                <button
                                  onClick={() => {
                                    navigate(
                                      `/edit-basic-details/${lead.leadUID}`,
                                    );
                                    setOpenActionMenu(null);
                                  }}
                                  className="w-full h-9 px-3 flex items-center gap-2 text-sm text-slate-700 hover:bg-violet-50 hover:text-violet-700 transition"
                                >
                                  <Pencil size={14} />
                                  <span>Edit</span>
                                </button>

                                {/* History */}
                                <button
                                  onClick={
                                    () => {
                                      setSelectedHistoryLead(lead);
                                      setOpenHistoryDrawer(true);
                                    }
                                  }
                                  className="w-full h-9 px-3 flex items-center gap-2 text-sm text-slate-700 hover:bg-violet-50 hover:text-violet-700 transition"
                                >
                                  <History size={14} />
                                  <span>View History</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* EXPANDED SECTION */}
                        <div
                          className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                            expandedLead === lead.leadUID
                              ? "max-h-[1200px] opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <div className="px-6 pb-4">
                                <div className="ml-13 rounded-xl p-[1px]">
                                  <div
                                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#edf4fb] via-[#f8fbff] to-white border border-slate-200/70
                                           shadow-[0_6px_24px_rgba(148,163,184,0.08)] backdrop-blur-sm"
                              >
                                <div className="px-5 py-4">
                                  {/* HEADER */}
                                  <div className="flex items-start justify-between gap-4 mb-6 pb-5 border-b border-slate-200/70">
                                    {/* ACTIONS - LEFT */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {/* VIEW RESUME */}
                                      <button
                                        onClick={() => {
                                          if (!hasResume) return;
                                          setSelectedResume(lead);
                                          setOpenResumeDrawer(true);
                                        }}
                                        disabled={!hasResume}
                                        className={`h-9 px-3 rounded-xl text-sm font-medium transition-all flex items-center gap-2 border
                                                    ${
                                                      hasResume
                                                        ? `border-slate-200 bg-white text-slate-700 shadow-sm hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700`
                                                        : `border-slate-300 bg-slate-200 text-slate-400 shadow-sm cursor-not-allowed`
                                                    }
                                                  `}
                                      >
                                        <Globe size={15} />
                                        Resume
                                      </button>

                                      {/* CALL */}
                                      <button
                                        onClick={() => {
                                          setCallLead(lead);
                                          setOpenCallDrawer(true);
                                        }}
                                        className="h-9 px-3 rounded-xl text-sm font-medium transition-all flex items-center gap-2 bg-gradient-to-b from-emerald-500 to-emerald-600
                                                 text-white shadow-sm hover:shadow-md hover:-translate-y-[1px]"
                                      >
                                        <Phone size={15} />
                                        Connect
                                      </button>

                                      
                                    </div>

                                    {/* RIGHT */}
                                    <div className="flex items-center">
                                      <div className="h-8 px-3 rounded-full flex items-center justify-center gap-2">
                                        {/* EDIT */}
                                      <button
                                        onClick={() =>
                                          navigate(
                                            `/edit-basic-details/${lead.leadUID}`,
                                          )
                                        }
                                        className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium
                                                 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700 transition-all flex items-center gap-2"
                                      >
                                        <Pencil size={15} />
                                        Edit
                                      </button>
                                          <button
                                        onClick={() => {
                                          setSelectedHistoryLead(lead);
                                          setOpenHistoryDrawer(true);
                                        }}
                                        className="h-9 px-3 rounded-xl bg-primary text-white text-sm font-medium flex items-center gap-2 shadow-sm hover:shadow-md transition-all"                                      >
                                        <History size={15} />
                                        View History
                                      </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* QUICK STATS */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                                    <div className="rounded-xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-[0_4px_20px_rgba(148,163,184,0.08)] hover:shadow-[0_8px_30px_rgba(59,130,246,0.12)] hover:-translate-y-0.5 transition-all duration-300 px-4 py-2.5">
                                      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                        Experience
                                      </p>

                                      <p className="text-sm font-semibold text-slate-800 mt-1.5">
                                        {lead.totalExperience
                                          ? `${lead.totalExperience} Years`
                                          : "-"}
                                      </p>
                                    </div>

                                    <div className="rounded-xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-[0_4px_20px_rgba(148,163,184,0.08)] hover:shadow-[0_8px_30px_rgba(59,130,246,0.12)] hover:-translate-y-0.5 transition-all duration-300 px-4 py-2.5">
                                      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                        Current CTC
                                      </p>

                                      <p className="text-sm font-semibold text-slate-800 mt-1.5">
                                        {lead.currentCTC
                                          ? formatCTC(lead.currentCTC)
                                          : "-"}
                                      </p>
                                    </div>

                                    <div className="rounded-xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-[0_4px_20px_rgba(148,163,184,0.08)] hover:shadow-[0_8px_30px_rgba(59,130,246,0.12)] hover:-translate-y-0.5 transition-all duration-300 px-4 py-2.5">
                                      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                        Expected CTC
                                      </p>

                                      <p className="text-sm font-semibold text-slate-800 mt-1.5">
                                        {lead.expectedCTC
                                          ? formatCTC(lead.expectedCTC)
                                          : "-"}
                                      </p>
                                    </div>

                                    <div className="rounded-xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-[0_4px_20px_rgba(148,163,184,0.08)] hover:shadow-[0_8px_30px_rgba(59,130,246,0.12)] hover:-translate-y-0.5 transition-all duration-300 px-4 py-2.5">
                                      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                        Preferred Location
                                      </p>

                                      <p className="text-sm font-semibold text-slate-800 mt-1.5 truncate">
                                        {lead.preferredLocation || "-"}
                                      </p>
                                    </div>
                                  </div>

                                  {/* SKILLS - MOVED UP */}
                                  <div className="mb-4">
                                    <div className="flex items-center justify-between mb-3">
                                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Skills
                                      </p>

                                      <span className="text-[11px] text-slate-400">
                                        {lead.skills
                                          ? lead.skills.split(",").length
                                          : 0}{" "}
                                        skills
                                      </span>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                      {lead.skills ? (
                                        lead.skills
                                          .split(",")
                                          .map((skill, idx) => (
                                            <span
                                              key={idx}
                                              className="px-2.5 py-1 rounded-xl text-xs font-medium bg-gradient-to-b from-white to-slate-50 border border-slate-200/70 shadow-sm hover:shadow-md hover:border-blue-200 text-slate-700 hover:bg-blue-50/60 transition-all"
                                            >
                                              {skill.trim()}
                                            </span>
                                          ))
                                      ) : (
                                        <span className="text-sm text-slate-400">
                                          No skills added
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* DETAILS */}
                                  <div className="rounded-3xl bg-white/70 backdrop-blur-md border border-white/70 shadow-[0_6px_25px_rgba(148,163,184,0.08)] overflow-hidden">
                                    <div className="divide-y divide-slate-100">
                                      <div className="grid md:grid-cols-3">
                                        <div className="px-4 py-2.5">
                                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                            Qualification
                                          </p>

                                          <p className="text-sm font-medium text-slate-700 mt-1">
                                            {lead.qualification || "-"}
                                          </p>
                                        </div>

                                        <div className="px-4 py-2.5 border-l border-slate-100">
                                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                            Designation
                                          </p>

                                          <p className="text-sm font-medium text-slate-700 mt-1">
                                            {lead.designation || "-"}
                                          </p>
                                        </div>

                                        <div className="px-4 py-2.5 border-l border-slate-100">
                                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                            Department
                                          </p>

                                          <p className="text-sm font-medium text-slate-700 mt-1">
                                            {lead.department || "-"}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="grid md:grid-cols-3">
                                        <div className="px-4 py-2.5">
                                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                            Current Company
                                          </p>

                                          <p className="text-sm font-medium text-slate-700 mt-1">
                                            {lead.currentCompany || "-"}
                                          </p>
                                        </div>

                                        <div className="px-4 py-2.5 border-l border-slate-100">
                                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                            Current Job Location
                                          </p>

                                          <p className="text-sm font-medium text-slate-700 mt-1">
                                            {lead.currentJobLocation || "-"}
                                          </p>
                                        </div>

                                        <div className="px-4 py-2.5 border-l border-slate-100">
                                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                            Reason of Leaving
                                          </p>

                                          <p className="text-sm font-medium text-slate-700 mt-1">
                                            {lead.reasonOfLeaving || "-"}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* LOCAL RESULTS FOOTER */}
                  {searchPerformed &&
                    !activeSearch &&
                    visibleLeads.length > 0 && (
                      <div className="border-t border-slate-200 bg-slate-50 px-6 py-5">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={searchDatabase}
                            disabled={dbSearching}
                            className="h-9 px-4 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                          >
                            {dbSearching ? "Loading..." : "Load more"}
                          </button>
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>

          {/* PAGINATION */}
          {!initialLoading && displayedTotalRecords > 0 && (
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 bg-white sticky bottom-0 flex-shrink-0">
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                {/* PREVIOUS */}
                <button
                  disabled={page === 1}
                  onClick={() => handlePageChange(page - 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center
                              ${
                                page === 1
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                            `}
                >
                  Prev
                </button>

                {(() => {
                  const pages = [];

                  for (let i = 1; i <= totalPages; i++) {
                    if (
                      i === 1 ||
                      i === totalPages ||
                      (i >= page - 1 && i <= page + 1)
                    ) {
                      pages.push(i);
                    }
                  }

                  return pages.map((pageNumber, index) => {
                    const prevPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {/* DOTS */}
                        {prevPage && pageNumber - prevPage > 1 && (
                          <span className="px-1 text-sm font-medium text-slate-600">
                            ...
                          </span>
                        )}

                        {/* PAGE BUTTON */}
                        <button
                          onClick={() => handlePageChange(pageNumber)}
                          className={`h-8 min-w-[34px] px-2.5 rounded-lg text-sm font-semibold transition-all duration-200
                                      ${
                                        page === pageNumber
                                          ? "bg-primary text-white shadow-sm"
                                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                      }
                                    `}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}

                {/* NEXT */}
                <button
                  disabled={page === totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center
                              ${
                                page === totalPages
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                            `}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PhoneVerificationDrawer
        open={openPhoneDrawer}
        onClose={() => {
          setOpenPhoneDrawer(false);
        }}
      />
      <InitiateCallDrawer
        open={openCallDrawer}
        onClose={() => {
          setOpenCallDrawer(false);
          setCallLead(null);
        }}
        lead={callLead}
      />
      <ViewResumeDrawer
        open={openResumeDrawer}
        onClose={() => {
          setOpenResumeDrawer(false);
          setSelectedResume(null);
        }}
        resumeUrl={selectedResume?.resumePath}
        candidateName={selectedResume?.name}
      />

      <RecruitmentHistoryDrawer
      open = {openHistoryDrawer}
      onClose = {() =>{
        setOpenHistoryDrawer(false);
        setSelectedHistoryLead(null);
      }}
      recruitmentUID = {selectedHistoryLead?.leadUID}
      
      />
      {/* <LeadDrawer open={!!selectedLead} lead={selectedLead} onClose={() => setSelectedLead(null)} /> */}
    </>
  );
};

export default MyLeads;
